/**
 * ISO 27001 Implementation Cost & Effort Calculation Engine
 *
 * Audit time follows the published ISO/IEC 27006-1 audit-time principles (see
 * docs/METHODOLOGY_AUDIT.md for provenance and open review items).
 * Uses a component-based cost model (labor + certification audit + training + tools)
 * instead of proportional weights.
 *
 * Single source of truth: every factor table used by the UI, the PDF export and the
 * benchmark comparison (`benchmarkData.js`) is defined and exported HERE. Lookup maps
 * are DERIVED from the labelled UI arrays so the two can never drift apart.
 */

// ---------- Shared Constants (used by UI components) ----------

export const COMPANY_SIZES = [
  { value: 'micro', label: 'Micro (1-10 employees)' },
  { value: 'small', label: 'Small (11-50)' },
  { value: 'medium', label: 'Medium (51-250)' },
  { value: 'large', label: 'Large (251-1000)' },
  { value: 'enterprise', label: 'Enterprise (1000+)' },
];

export const INDUSTRIES = [
  { value: 'finance', label: 'Finance & Banking', multiplier: 1.45 },
  { value: 'healthcare', label: 'Healthcare', multiplier: 1.35 },
  { value: 'government', label: 'Government', multiplier: 1.30 },
  { value: 'technology', label: 'Technology', multiplier: 1.05 },
  { value: 'manufacturing', label: 'Manufacturing', multiplier: 1.22 },
  { value: 'retail', label: 'Retail & E-commerce', multiplier: 1.10 },
  { value: 'education', label: 'Education', multiplier: 1.08 },
  { value: 'energy', label: 'Energy & Utilities', multiplier: 1.25 },
  { value: 'telecom', label: 'Telecom & Media', multiplier: 1.20 },
  { value: 'insurance', label: 'Insurance & Legal', multiplier: 1.38 },
  { value: 'logistics', label: 'Logistics & Transport', multiplier: 1.15 },
  { value: 'other', label: 'Other', multiplier: 1.00 },
];

export const INFRASTRUCTURE_TYPES = [
  { value: 'cloud', label: 'Cloud', multiplier: 0.90 },
  { value: 'onpremise', label: 'On-Premise', multiplier: 1.15 },
  { value: 'hybrid', label: 'Hybrid', multiplier: 1.0 },
];

export const EXISTING_CERTS = [
  { value: 'soc2', label: 'SOC 2', reduction: 0.10 },
  { value: 'gdpr', label: 'GDPR', reduction: 0.08 },
  { value: 'iso9001', label: 'ISO 9001', reduction: 0.06 },
  { value: 'nist', label: 'NIST CSF', reduction: 0.07 },
  { value: 'pcidss', label: 'PCI DSS', reduction: 0.05 },
  { value: 'hipaa', label: 'HIPAA', reduction: 0.06 },
];

export const CURRENCIES = [
  { value: 'EUR', symbol: '\u20AC', label: 'EUR (\u20AC)' },
  { value: 'USD', symbol: '$', label: 'USD ($)' },
  { value: 'GBP', symbol: '\u00A3', label: 'GBP (\u00A3)' },
  { value: 'AUD', symbol: 'A$', label: 'AUD (A$)' },
  { value: 'CAD', symbol: 'C$', label: 'CAD (C$)' },
  { value: 'CHF', symbol: 'Fr', label: 'CHF (Fr)' },
  { value: 'JPY', symbol: '\u00A5', label: 'JPY (\u00A5)' },
  { value: 'INR', symbol: '\u20B9', label: 'INR (\u20B9)' },
];

// --- ISO/IEC 27001:2022 Annex A — 4 themes / 93 controls ---
// The advanced maturity assessment is organised around the 2022 structure.
// `isNew: true` marks a control group that contains one of the 11 controls
// introduced in the 2022 revision (shown with a "New in 2022" badge in the UI).
export const ISO_2022_THEMES = [
  {
    id: 'organizational', code: 'A.5', controlCount: 37,
    groups: [
      { id: 'org_policies', code: 'A.5.1–A.5.4' },
      { id: 'org_threat_intel', code: 'A.5.7', isNew: true, newControl: 'A.5.7 Threat intelligence' },
      { id: 'org_asset_mgmt', code: 'A.5.9–A.5.14' },
      { id: 'org_access_identity', code: 'A.5.15–A.5.18' },
      { id: 'org_supplier_cloud', code: 'A.5.19–A.5.23', isNew: true, newControl: 'A.5.23 Information security for use of cloud services' },
      { id: 'org_incident', code: 'A.5.24–A.5.28' },
      { id: 'org_continuity', code: 'A.5.29–A.5.30', isNew: true, newControl: 'A.5.30 ICT readiness for business continuity' },
      { id: 'org_legal_privacy', code: 'A.5.31–A.5.37' },
    ],
  },
  {
    id: 'people', code: 'A.6', controlCount: 8,
    groups: [
      { id: 'ppl_screening', code: 'A.6.1–A.6.2' },
      { id: 'ppl_awareness', code: 'A.6.3' },
      { id: 'ppl_disciplinary', code: 'A.6.4–A.6.5' },
      { id: 'ppl_nda_remote', code: 'A.6.6–A.6.7' },
      { id: 'ppl_event_reporting', code: 'A.6.8' },
    ],
  },
  {
    id: 'physical', code: 'A.7', controlCount: 14,
    groups: [
      { id: 'phy_perimeter_entry', code: 'A.7.1–A.7.2' },
      { id: 'phy_offices_monitoring', code: 'A.7.3–A.7.4', isNew: true, newControl: 'A.7.4 Physical security monitoring' },
      { id: 'phy_environmental', code: 'A.7.5–A.7.8' },
      { id: 'phy_equipment', code: 'A.7.9–A.7.13' },
      { id: 'phy_disposal_cleardesk', code: 'A.7.7, A.7.10, A.7.14' },
    ],
  },
  {
    id: 'technological', code: 'A.8', controlCount: 34,
    groups: [
      { id: 'tech_endpoint_access', code: 'A.8.1–A.8.5' },
      { id: 'tech_malware_vuln', code: 'A.8.6–A.8.8' },
      { id: 'tech_config_mgmt', code: 'A.8.9', isNew: true, newControl: 'A.8.9 Configuration management' },
      { id: 'tech_data_protection', code: 'A.8.10–A.8.12', isNew: true, newControl: 'A.8.10 Information deletion · A.8.11 Data masking · A.8.12 Data leakage prevention' },
      { id: 'tech_backup_redundancy', code: 'A.8.13–A.8.14' },
      { id: 'tech_logging_monitoring', code: 'A.8.15–A.8.16', isNew: true, newControl: 'A.8.16 Monitoring activities' },
      { id: 'tech_network_web', code: 'A.8.20–A.8.23', isNew: true, newControl: 'A.8.23 Web filtering' },
      { id: 'tech_secure_dev', code: 'A.8.25–A.8.28', isNew: true, newControl: 'A.8.28 Secure coding' },
    ],
  },
];

// Flattened list of all 2022 control groups (for iteration / averaging).
export const ISO_2022_GROUPS = ISO_2022_THEMES.flatMap(t => t.groups.map(g => ({ ...g, themeId: t.id })));

// Number of brand-new controls introduced in ISO 27001:2022.
export const NEW_2022_CONTROL_COUNT = 11;

// Expand 4 theme-level scores (from the AI assessment) into a per-control-group
// map so they can pre-fill the advanced maturity assessment.
export function expandThemeScoresToGroups(themeScores = {}) {
  const out = {};
  ISO_2022_THEMES.forEach(theme => {
    const score = themeScores[theme.id];
    if (typeof score === 'number') {
      theme.groups.forEach(g => { out[g.id] = score; });
    }
  });
  return out;
}

export const MATURITY_LEVELS = {
  1: { label: 'Initial', factor: 1.35 },
  2: { label: 'Developing', factor: 1.15 },
  3: { label: 'Defined', factor: 1.0 },
  4: { label: 'Managed', factor: 0.80 },
  5: { label: 'Optimized', factor: 0.65 },
};

export const FX_RATES = { EUR: 1, USD: 1.08, GBP: 0.86, AUD: 1.65, CAD: 1.47, CHF: 0.94, JPY: 162, INR: 90 };

// ---------- Engine identity + estimate-confidence model ----------
// The browser engine is a PREVIEW engine. Every saved/shared/exported estimate is
// recalculated by the backend (services/cost_engine.py, same constants + version).
export const ENGINE_VERSION = 'ISO_COST_ENGINE_2_0';

// Model-confidence indicator (NOT a statistical confidence interval): the
// planning range widens when inputs are mostly model assumptions.
export const CONFIDENCE_SIGNALS = [
  'headcount', 'locations', 'itSystems', 'infrastructure', 'maturity',
  'controlReuse', 'deliveryModel', 'labourRates', 'sector', 'targetTiming',
];
export const CONFIDENCE_BANDS = [
  { level: 'high', minScore: 0.75, rangeLow: 0.88, rangeHigh: 1.15 },
  { level: 'moderate', minScore: 0.50, rangeLow: 0.80, rangeHigh: 1.25 },
  { level: 'low', minScore: 0.00, rangeLow: 0.68, rangeHigh: 1.45 },
];
const SPECIFIC_SIGNAL_THRESHOLD = 0.75;

// ---------- Derived lookup maps (single source of truth: the arrays above) ----------

const INDUSTRY_MULTIPLIERS = Object.fromEntries(INDUSTRIES.map(i => [i.value, i.multiplier]));
const INFRA_MULTIPLIERS = Object.fromEntries(INFRASTRUCTURE_TYPES.map(i => [i.value, i.multiplier]));
export const CERT_REDUCTIONS = Object.fromEntries(EXISTING_CERTS.map(c => [c.value, c.reduction]));
export const MATURITY_FACTORS = Object.fromEntries(
  Object.entries(MATURITY_LEVELS).map(([lvl, v]) => [lvl, v.factor])
);

// Base implementation effort in person-days (recalibrated to market reality)
const BASE_EFFORTS = {
  micro:      5.5,  // 1-10 employees   → ~6-12 days with cloud-native scope & GRC tooling
  small:      25,   // 11-50 employees  → ~25-35 days typical
  medium:     70,   // 51-250 employees → ~70-120 days typical
  large:      180,  // 251-1000         → ~180-280 days typical
  enterprise: 400,  // 1000+            → ~400-650 days typical
};

// Irreducible ISMS core: even a best-case org (high maturity + certs + cloud) must
// still produce the risk assessment, Statement of Applicability, mandatory documents,
// internal audit, management review and support Stage 1+2. These floors stop the
// multiplier stack from producing impossible totals (e.g. 2 person-days).
const MIN_EFFORTS = {
  micro:      5,
  small:      12,
  medium:     30,
  large:      75,
  enterprise: 160,
};

// Industry calendar duration overhead (applied on top of effort-based duration)
// Captures approval cycles, procurement delays, change management complexity
// that stretch calendar time regardless of person-day effort.
export const INDUSTRY_DURATION_FACTORS = {
  finance:       1.25,  // Regulatory approval cycles, sign-off chains
  healthcare:    1.20,  // Patient safety reviews, extensive change management
  government:    1.30,  // Procurement rules, bureaucratic sign-off processes
  energy:        1.15,  // Safety compliance and operational continuity reviews
  insurance:     1.20,  // Regulatory complexity, actuarial approvals
  telecom:       1.10,  // Multi-system coordination and regulatory filings
  manufacturing: 1.05,  // Operational continuity constraints
  logistics:     1.05,  // Cross-site coordination
  technology:    1.00,  // Baseline — agile culture, faster decisions
  retail:        1.00,
  education:     1.00,
  other:         1.00,
};

// Maturity direct calendar factor (applied on top of effort-based duration)
// Low-maturity orgs face rework loops, change resistance, slower decisions.
export const MATURITY_DURATION_FACTORS = {
  1: 1.20,  // Initial: significant rework, resistance, slow sign-off
  2: 1.10,  // Developing: some process friction
  3: 1.00,  // Defined: baseline
  4: 0.95,  // Managed: streamlined decisions
  5: 0.90,  // Optimized: fast execution, minimal overhead
};

// ISMSCalculator audit-time model (ISO-DERIVED): initial certification audit
// duration (Stage 1 + Stage 2) by effective number of persons. Follows the
// published ISO/IEC 27006-1 audit-time principles; no ISO table is reproduced.
// Open review items: docs/METHODOLOGY_AUDIT.md
// by effective number of persons doing work under the ISMS. Exact table values,
// including the half-day steps.
const ISO_27006_AUDIT_TABLE = [
  { maxFte: 10,   days: 5 },
  { maxFte: 25,   days: 7 },
  { maxFte: 45,   days: 8.5 },
  { maxFte: 65,   days: 10 },
  { maxFte: 85,   days: 11 },
  { maxFte: 125,  days: 12 },
  { maxFte: 175,  days: 13 },
  { maxFte: 275,  days: 14 },
  { maxFte: 425,  days: 15 },
  { maxFte: 625,  days: 16.5 },
  { maxFte: 875,  days: 17.5 },
  { maxFte: 1175, days: 18.5 },
  { maxFte: 1550, days: 19.5 },
  { maxFte: 2025, days: 21 },
  { maxFte: 2675, days: 22 },
  { maxFte: Infinity, days: 23 },
];

// Fixed training costs by company size (EUR)
const TRAINING_COSTS = {
  micro:      400,
  small:      2000,
  medium:     8000,
  large:      18000,
  enterprise: 40000,
};

// Fixed tool/software costs for entire implementation period (EUR)
// Micro reflects SaaS GRC tool subscription (Vanta/Drata tier), not a large one-time purchase
const TOOLS_COSTS = {
  micro:      500,
  small:      5000,
  medium:     20000,
  large:      60000,
  enterprise: 150000,
};

// Audit reduction factors by company size.
// Adjustment ceiling: never reduce audit time by more than 30% (retention >= 0.70).
// Reductions are justified by narrow scope, focused ISMS, or cloud-only infrastructure.
// Proprietary model limit: no company size goes below 0.70 (i.e. a 30% reduction cap).
const AUDIT_REDUCTION_FACTORS = {
  micro:      0.70,   // 30% reduction — model ceiling (e.g. cloud-only, focused scope)
  small:      0.75,   // 25% reduction
  medium:     0.80,   // 20% reduction
  large:      0.80,   // 20% reduction
  enterprise: 0.80,   // 20% reduction
};

// ISMSCalculator floor (MODELLED, not normative): smallest laddered duration with the
// maximum 30% adjustment applied.
// This floor is applied after the reduction factor as a safety net.
const MIN_AUDIT_DAYS = 3.5;

// Accredited certification-body day rate (EUR). Deliberately INDEPENDENT of the
// user's consultant rate — CB fees don't change because your implementation
// consultant is cheaper or pricier.
export const CERT_BODY_DAY_RATE = 1300;

// Default rates (EUR)
export const DEFAULT_INTERNAL_RATE = 600;
export const DEFAULT_CONSULTANT_RATE = 1200;

// Consultant-led engagements still require internal ownership: scope decisions,
// evidence gathering, interviews, management review. Matches the UI promise of
// "80% consultant, 20% internal oversight".
const CONSULTANT_LED_INTERNAL_SHARE = 0.20;

// Phase distribution (percentage of total effort)
const PHASES = [
  { id: 'gap_analysis',          pct: 0.10 },
  { id: 'risk_assessment',       pct: 0.15 },
  { id: 'policy_development',    pct: 0.20 },
  { id: 'implementation',        pct: 0.30 },
  { id: 'internal_audit',        pct: 0.10 },
  { id: 'certification_audit',   pct: 0.15 },
];

// ---------- Timeline model ----------
// durationWeeks = (CALENDAR_BASE_WEEKS + effort / (TEAM_PARALLELISM × 4 productive
// days/week)) × industry × maturity × approach factors, clamped to per-size
// guardrails. The fixed base captures elapsed (non-effort) time every project
// pays: scoping, evidence-collection wait states, CB audit scheduling lead time.
const CALENDAR_BASE_WEEKS = { micro: 8, small: 10, medium: 14, large: 18, enterprise: 26 };
// Average productive FTEs working the implementation simultaneously.
const TEAM_PARALLELISM = { micro: 0.5, small: 1.0, medium: 1.5, large: 2.5, enterprise: 3.5 };
// Wide guardrails only — the formula should drive the result in normal scenarios.
const MIN_DURATION_WEEKS = { micro: 6, small: 10, medium: 16, large: 24, enterprise: 40 };
const MAX_DURATION_WEEKS = { micro: 26, small: 44, medium: 78, large: 104, enterprise: 130 };

// ---------- Helper Functions ----------

function getIso27006AuditDays(fte) {
  for (const row of ISO_27006_AUDIT_TABLE) {
    if (fte <= row.maxFte) return row.days;
  }
  return 23;
}

function estimateFteFromSize(size) {
  const fteMap = { micro: 5, small: 30, medium: 150, large: 500, enterprise: 2000 };
  return fteMap[size] || 50;
}

// Linear interpolation across an integer-keyed factor table (1-5), so a
// fractional maturity (e.g. 3.2 from the advanced per-control assessment)
// maps to a smooth factor instead of snapping to a rounded level.
export function interpolateFactor(table, level) {
  const lvl = Math.max(1, Math.min(5, level));
  const lo = Math.floor(lvl);
  const hi = Math.ceil(lvl);
  if (lo === hi) return table[lo] ?? 1.0;
  const frac = lvl - lo;
  return table[lo] + (table[hi] - table[lo]) * frac;
}

/**
 * Effective maturity level (float, 1-5) for a calculator config.
 * Advanced mode averages the per-control-group scores; simple mode uses the
 * overall slider. Shared by the engine, ResultsPanel hints, PDF export and the
 * benchmark comparison so they can never disagree.
 */
export function getEffectiveMaturity(config = {}) {
  const { maturityMode, domainMaturity, overallMaturity } = config;
  if (maturityMode === 'advanced' && domainMaturity && Object.keys(domainMaturity).length > 0) {
    const scores = Object.values(domainMaturity).map(d =>
      typeof d === 'object' ? ((d.design || 2) + (d.operational || 2)) / 2 : d
    );
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  }
  return overallMaturity || 3;
}

// ---------- Main Calculation ----------

const r1 = (v) => Math.round(v * 10) / 10;
const r2 = (v) => Math.round(v * 100) / 100;
const r3 = (v) => Math.round(v * 1000) / 1000;

/**
 * Estimate confidence: how organisation-specific the planning inputs are.
 * Mirrors backend `services/cost_engine.assess_confidence` exactly.
 */
export function assessConfidence(config = {}) {
  const locations = config.locations ?? 1;
  const itSystems = config.itSystems ?? 5;
  const approach = config.approach || 'mixed';
  const mixRatio = config.mixRatio ?? config.internalSplit ?? 50;
  const certs = config.existingCerts || [];
  const overall = config.overallMaturity ?? 3;

  const scores = {
    headcount: config.employees ? 1.0 : 0.0,
    locations: locations !== 1 ? 1.0 : 0.5,
    itSystems: itSystems !== 5 ? 1.0 : 0.5,
    infrastructure: (config.infraType || 'hybrid') !== 'hybrid' ? 1.0 : 0.5,
    maturity: 0.25,
    controlReuse: certs.length > 0 ? 1.0 : 0.5,
    deliveryModel: 0.5,
    labourRates: (config.internalRate || config.consultantRate) ? 1.0 : 0.0,
    sector: (config.industry || 'technology') !== 'other' ? 1.0 : 0.25,
    targetTiming: config.targetMonths ? 1.0 : 0.25,
  };
  if (config.maturityMode === 'advanced' && config.domainMaturity && Object.keys(config.domainMaturity).length > 0) {
    scores.maturity = 1.0;
  } else if (Math.abs(overall - 3) > 0.01) {
    scores.maturity = 0.75;
  }
  if (approach !== 'mixed') scores.deliveryModel = 1.0;
  else if (Math.abs(mixRatio - 50) > 0.01) scores.deliveryModel = 0.75;

  const total = CONFIDENCE_SIGNALS.length;
  const score = CONFIDENCE_SIGNALS.reduce((sum, s) => sum + scores[s], 0) / total;
  const specificInputs = CONFIDENCE_SIGNALS.filter(s => scores[s] >= SPECIFIC_SIGNAL_THRESHOLD).length;
  const gaps = CONFIDENCE_SIGNALS.filter(s => scores[s] < SPECIFIC_SIGNAL_THRESHOLD);
  const band = CONFIDENCE_BANDS.find(b => score >= b.minScore) || CONFIDENCE_BANDS[CONFIDENCE_BANDS.length - 1];

  return {
    level: band.level,
    score: r2(score),
    specificInputs,
    totalInputs: total,
    gaps,
    signals: scores,
    rangeLowFactor: band.rangeLow,
    rangeHighFactor: band.rangeHigh,
  };
}

// Neutral reference scenario per driver — mirrors the backend driver table.
const DRIVER_REFERENCES = [
  ['maturity', { overallMaturity: 3, maturityMode: 'simple', domainMaturity: null }],
  ['sector', { industry: 'other' }],
  ['locations', { locations: 1 }],
  ['itSystems', { itSystems: 5 }],
  ['infrastructure', { infraType: 'hybrid' }],
  ['controlReuse', { existingCerts: [] }],
  ['deliveryModel', { approach: 'mixed', mixRatio: 50, internalSplit: 50 }],
  ['labourRates', { internalRate: null, consultantRate: null }],
];

function costDrivers(config, baseline) {
  const drivers = [];
  DRIVER_REFERENCES.forEach(([id, reference]) => {
    const neutral = calculateEstimate({ ...config, ...reference }, false);
    const modelledEffortDelta = baseline.totalEffort - neutral.totalEffort;
    const modelledCostDelta = baseline.totalCost - neutral.totalCost;
    if (modelledEffortDelta === 0 && modelledCostDelta === 0) return;
    drivers.push({
      id,
      modelledEffortDelta,
      modelledCostDelta,
      direction: modelledCostDelta > 0 ? 'increase' : 'decrease',
    });
  });
  return drivers.sort((a, b) => Math.abs(b.modelledCostDelta) - Math.abs(a.modelledCostDelta)).slice(0, 5);
}

/**
 * Canonical decision-relevant assumptions behind an estimate.
 * Mirrored exactly by backend `services/cost_engine._assumptions` (same ids,
 * same order, same values) so the results panel, shared estimate and PDF
 * export can never drift. Transparency metadata only: nothing here feeds the
 * calculation.
 */
function buildAssumptions(config, result) {
  const m = result.methodology;
  const certs = config.existingCerts || [];
  const approach = config.approach || 'mixed';
  const mixRatio = config.mixRatio ?? config.internalSplit ?? 50;
  const overall = config.overallMaturity ?? 3;
  const maturitySpecific = (config.maturityMode === 'advanced'
    && config.domainMaturity && Object.keys(config.domainMaturity).length > 0)
    || Math.abs(overall - 3) > 0.01;
  const deliverySpecific = approach !== 'mixed' || Math.abs(mixRatio - 50) > 0.01;
  // Effective (as-calculated) site count: the engine always works with at least one site.
  const locationsEffective = Math.max(1, Math.floor(config.locations ?? 1));

  return [
    { id: 'effectivePersonnel', value: m.effectivePersonnel, provenance: config.employees ? 'USER_PROVIDED' : 'MODELLED' },
    { id: 'locations', value: locationsEffective, provenance: locationsEffective !== 1 ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION' },
    { id: 'infrastructure', value: config.infraType || 'hybrid', provenance: (config.infraType || 'hybrid') !== 'hybrid' ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION' },
    { id: 'maturityLevel', value: m.maturityLevel, provenance: maturitySpecific ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION' },
    { id: 'controlReuse', value: m.reuseReductionPct, provenance: certs.length > 0 ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION' },
    { id: 'deliveryModel', value: m.internalSharePct, provenance: deliverySpecific ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION' },
    { id: 'internalRate', value: m.internalRate, provenance: config.internalRate ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION' },
    { id: 'consultantRate', value: m.consultantRate, provenance: config.consultantRate ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION' },
    { id: 'certBodyDayRate', value: m.certBodyDayRate, provenance: config.certBodyDayRate != null ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION' },
    { id: 'auditDays', value: result.auditDays, provenance: 'ISO_DERIVED' },
    { id: 'trainingCost', value: m.trainingCost, provenance: config.trainingCost != null ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION' },
    { id: 'toolsCost', value: m.toolsCost, provenance: config.toolsCost != null ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION' },
    { id: 'durationWeeks', value: result.totalDurationWeeks, provenance: 'MODELLED' },
    { id: 'planningRange', value: [result.costRange.low, result.costRange.high], provenance: 'ESTIMATED' },
  ];
}

export function calculateEstimate(config, withAnalysis = true) {
  const {
    companySize = 'small',
    industry = 'technology',
    locations = 1,
    itSystems = 5,
    infraType = 'hybrid',
    existingCerts = [],
    approach = 'mixed',
    internalSplit = 50,
    mixRatio,
    internalRate,
    consultantRate,
    currency = 'EUR',
    employees,
    // Phase 2 inline assumption overrides: substitute a modelled default with a
    // user planning input for THIS estimate only. Mirrors backend cost_engine.
    certBodyDayRate,
    trainingCost: trainingCostOverride,
    toolsCost: toolsCostOverride,
  } = config;

  const intRate = internalRate || DEFAULT_INTERNAL_RATE;
  const conRate = consultantRate || DEFAULT_CONSULTANT_RATE;

  // --- Determine approach split ---
  // 'consultant' is the value the UI radio emits; 'external' kept for saved estimates.
  let internalPct;
  if (approach === 'internal') internalPct = 1.0;
  else if (approach === 'consultant' || approach === 'external') internalPct = CONSULTANT_LED_INTERNAL_SHARE;
  else internalPct = (mixRatio ?? internalSplit ?? 50) / 100;

  // --- Maturity level (float average so single-slider changes are reflected) ---
  const maturityLevel = getEffectiveMaturity(config);

  // --- Effort Calculation ---
  const baseEffort = BASE_EFFORTS[companySize] || BASE_EFFORTS.small;
  const industryMultiplier = INDUSTRY_MULTIPLIERS[industry] || 1.0;
  const locationMultiplier = 1 + Math.log2(Math.max(1, locations)) * 0.12;
  const itSystemsMultiplier = 0.85 + Math.log2(Math.max(1, itSystems)) * 0.08;
  const maturityFactor = interpolateFactor(MATURITY_FACTORS, maturityLevel);
  const infraMultiplier = INFRA_MULTIPLIERS[infraType] || 1.0;

  let certReduction = 0;
  (existingCerts || []).forEach(cert => {
    certReduction += CERT_REDUCTIONS[cert] || 0;
  });
  certReduction = Math.min(certReduction, 0.30); // Cap at 30%

  const rawEffort = baseEffort * industryMultiplier * locationMultiplier *
    itSystemsMultiplier * maturityFactor * infraMultiplier * (1 - certReduction);
  const minEffort = MIN_EFFORTS[companySize] || MIN_EFFORTS.small;
  const totalEffort = Math.max(minEffort, Math.round(rawEffort));

  // --- Split by approach ---
  const internalDays = Math.round(totalEffort * internalPct);
  const consultantDays = totalEffort - internalDays;

  // --- Labor Cost ---
  const laborCost = (internalDays * intRate) + (consultantDays * conRate);

  // --- Audit Cost (ISO 27006) ---
  // ISO/IEC 27006: max 30% reduction, absolute minimum 3.5 days.
  // Multi-site sampling: certification bodies audit ~√n sites for similar
  // locations; each additional sampled site adds roughly one on-site day.
  const fte = employees || estimateFteFromSize(companySize);
  const rawAuditDays = getIso27006AuditDays(fte);
  const auditReductionFactor = AUDIT_REDUCTION_FACTORS[companySize] || 0.80;
  const sampledSites = Math.ceil(Math.sqrt(Math.max(1, locations)));
  const multiSiteDays = (sampledSites - 1) * 1.0;
  const auditDays = Math.max(
    MIN_AUDIT_DAYS,
    Math.round((rawAuditDays * auditReductionFactor + multiSiteDays) * 10) / 10
  );
  const cbDayRate = certBodyDayRate == null ? CERT_BODY_DAY_RATE : certBodyDayRate;
  const auditCost = Math.round(auditDays * cbDayRate);

  // --- Training Cost ---
  const trainingCost = trainingCostOverride == null
    ? (TRAINING_COSTS[companySize] || TRAINING_COSTS.small)
    : trainingCostOverride;

  // --- Tools / Software Cost ---
  const toolsCost = toolsCostOverride == null
    ? (TOOLS_COSTS[companySize] || TOOLS_COSTS.small)
    : toolsCostOverride;

  // --- Total ---
  const totalCost = laborCost + auditCost + trainingCost + toolsCost;

  // --- Currency conversion ---
  const fx = FX_RATES[currency] || 1;
  const convert = (v) => Math.round(v * fx);

  const costBreakdown = {
    labor:    convert(laborCost),
    audit:    convert(auditCost),
    training: convert(trainingCost),
    tools:    convert(toolsCost),
  };

  const convertedTotal = convert(totalCost);
  const confidence = assessConfidence(config);
  const costRange = {
    low:  Math.round(convertedTotal * confidence.rangeLowFactor),
    high: Math.round(convertedTotal * confidence.rangeHighFactor),
  };

  // --- Timeline ---
  // Industry calendar overhead: regulated sectors add approval cycles, procurement delays,
  // change management complexity — independent of the raw person-day effort.
  const industryDurationFactor = INDUSTRY_DURATION_FACTORS[industry] || 1.0;

  // Maturity calendar overhead — interpolated so fractional (advanced-mode)
  // maturity levels affect the timeline exactly like integer ones.
  const maturityDurationFactor = interpolateFactor(MATURITY_DURATION_FACTORS, maturityLevel);

  // Approach calendar factor: consultant-led engagements compress the calendar
  // (experience, templates, full-time focus); internal-only stretches it
  // (part-time staffing, learning curve). Continuous over the mix slider:
  // 100% internal → 1.15, 50/50 → 1.00, consultant-led (20% internal) → 0.91.
  const approachDurationFactor = 0.85 + 0.30 * internalPct;

  const baseWeeks = CALENDAR_BASE_WEEKS[companySize] || CALENDAR_BASE_WEEKS.small;
  const teamSize = TEAM_PARALLELISM[companySize] || 1.5;
  const rawDurationWeeks = Math.round(
    (baseWeeks + totalEffort / (teamSize * 4)) *
    industryDurationFactor * maturityDurationFactor * approachDurationFactor
  );
  const minDur = MIN_DURATION_WEEKS[companySize] || 10;
  const maxDur = MAX_DURATION_WEEKS[companySize] || 78;
  const durationWeeks = Math.max(minDur, Math.min(maxDur, rawDurationWeeks));
  // FTE = total effort / (calendar weeks × 4 productive days/week)
  const fteRequired = Math.max(0.1, Math.round((totalEffort / (durationWeeks * 4)) * 10) / 10);

  // --- Phases ---
  const phases = PHASES.map(p => {
    const phaseEffort = Math.max(1, Math.round(totalEffort * p.pct));
    const phaseWeeks = Math.max(1, Math.round(durationWeeks * p.pct));
    return {
      id: p.id,
      effort: phaseEffort,
      durationWeeks: phaseWeeks,
      percentOfEffort: Math.round(p.pct * 100),
    };
  });

  const result = {
    totalEffort,
    totalCost: convertedTotal,
    costRange,
    costBreakdown,
    internalDays,
    consultantDays,
    totalDurationWeeks: durationWeeks,
    fteRequired,
    auditDays,
    phases,
    certReduction: Math.round(certReduction * 100) / 100,
    confidence,
    engineVersion: ENGINE_VERSION,
    methodology: {
      engineVersion: ENGINE_VERSION,
      companySize,
      baseEffort,
      industryFactor: industryMultiplier,
      locationFactor: r3(locationMultiplier),
      itSystemsFactor: r3(itSystemsMultiplier),
      maturityLevel: r2(maturityLevel),
      maturityFactor: r3(maturityFactor),
      infraFactor: infraMultiplier,
      reuseReductionPct: r1(certReduction * 100),
      rawEffort: r1(rawEffort),
      effortFloor: minEffort,
      internalSharePct: Math.round(internalPct * 100),
      internalRate: intRate,
      consultantRate: conRate,
      effectivePersonnel: fte,
      auditLadderDays: rawAuditDays,
      auditAdjustmentFactor: auditReductionFactor,
      sampledSites,
      certBodyDayRate: cbDayRate,
      trainingCost,
      toolsCost,
      calendarBaseWeeks: baseWeeks,
      teamParallelism: teamSize,
      industryDurationFactor,
      maturityDurationFactor: r3(maturityDurationFactor),
      approachDurationFactor: r3(approachDurationFactor),
      currency,
      fxRate: fx,
    },
  };

  if (withAnalysis) {
    result.drivers = costDrivers(config, result);
    result.assumptions = buildAssumptions(config, result);
  }
  return result;
}
